import React, { memo } from 'react';

const GradientOverlay = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const [gradientValues, setGradientValues] = React.useState({
        color1: { h: 182, s: 65, l: 57 },
        color2: { h: 255, s: 65, l: 50 }
    });

    React.useEffect(() => {
        if (prefersReducedMotion) return;

        const interval = setInterval(() => {
            setGradientValues(prev => ({
                color1: { ...prev.color1, h: (prev.color1.h + 0.35) % 360 },
                color2: { ...prev.color2, h: (prev.color2.h + 0.35) % 360 }
            }));
        }, 50);

        return () => clearInterval(interval);
    }, [prefersReducedMotion]);

    return (
        <div
            className="absolute inset-0"
            style={{
                background: `linear-gradient(45deg,
                    hsla(${gradientValues.color1.h}, ${gradientValues.color1.s}%, ${gradientValues.color1.l}%, 0.14),
                    hsla(${gradientValues.color2.h}, ${gradientValues.color2.s}%, ${gradientValues.color2.l}%, 0.14))`
            }}
        />
    );
};

const WhimsicalBg = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    return (
        <div className="fixed inset-0 overflow-hidden -z-10">
            <GradientOverlay />

            {/* Stars */}
            <div className="absolute inset-0">
                {[...Array(15)].map((_, i) => (
                    <div
                        key={i}
                        className={`absolute ${!prefersReducedMotion && 'animate-twinkle'}`}
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 3}s`,
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" className="fill-primary/50">
                            <path d="M12 0 L13.5 9 L22.5 12 L13.5 15 L12 24 L10.5 15 L1.5 12 L10.5 9 L12 0Z" />
                        </svg>
                    </div>
                ))}
            </div>

            {/* Colored circles */}
            <div className="absolute inset-0">
                <div className={`absolute top-0 left-0 w-32 h-32 md:w-48 md:h-48 border-primary/40 border-4 rounded-full -translate-x-1/2 -translate-y-1/2 ${!prefersReducedMotion && 'animate-spin-slow'}`} />
                <div className={`absolute top-0 right-0 w-40 h-40 md:w-56 md:h-56 border-accent/40 border-4 rounded-full translate-x-1/2 -translate-y-1/2 ${!prefersReducedMotion && 'animate-spin-slow-reverse'}`} />
                <div className={`absolute bottom-0 left-1/4 w-24 h-24 md:w-40 md:h-40 border-secondary/40 border-4 rounded-full translate-y-1/2 ${!prefersReducedMotion && 'animate-spin-slow'}`} />
            </div>

            {/* Floating shapes */}
            <div className="absolute inset-0">
                <div className={`absolute top-1/4 right-1/4 w-8 h-8 rotate-45 bg-primary/30 ${!prefersReducedMotion && 'animate-float'}`}
                     style={{ animationDelay: '1s' }} />
                <div className={`absolute top-3/4 left-1/3 w-6 h-6 bg-accent/30 rounded-full ${!prefersReducedMotion && 'animate-float-alt'}`}
                     style={{ animationDelay: '2s' }} />
                <div className={`absolute bottom-1/4 right-1/3 w-10 h-10 rotate-12 bg-secondary/30 ${!prefersReducedMotion && 'animate-float'}`}
                     style={{ animationDelay: '0s' }} />
            </div>

            {/* Floating bubbles */}
            {!prefersReducedMotion && [...Array(8)].map((_, i) => (
                <div
                    key={`bubble-${i}`}
                    className="absolute w-4 h-4 rounded-full bg-gradient-to-br from-primary/30 to-accent/30 animate-float-bubble"
                    style={{
                        left: `${Math.random() * 100}%`,
                        animationDelay: `${Math.random() * 10}s`,
                        animationDuration: `${15 + Math.random() * 10}s`
                    }}
                />
            ))}

            {/* Wave pattern */}
            <div className="absolute bottom-0 left-0 right-0 h-48 md:h-64">
                <svg
                    className="absolute bottom-0 w-full h-full opacity-30"
                    viewBox="0 0 1440 320"
                    preserveAspectRatio="none"
                >
                    <path
                        className="fill-primary/20"
                        d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,90.7C672,85,768,107,864,128C960,149,1056,171,1152,165.3C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                    />
                    <path
                        className="fill-accent/20"
                        d="M0,192L48,181.3C96,171,192,149,288,154.7C384,160,480,192,576,192C672,192,768,160,864,154.7C960,149,1056,171,1152,181.3C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                    />
                </svg>
            </div>

            <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        .animate-twinkle {
          animation: twinkle 3s ease-in-out infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        @keyframes float-alt {
          0%, 100% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(20px) rotate(-5deg); }
        }
        .animate-float-alt {
          animation: float-alt 7s ease-in-out infinite;
        }
        @keyframes float-bubble {
          0% { transform: translateY(100vh); }
          100% { transform: translateY(-100px); }
        }
        .animate-float-bubble {
          animation: float-bubble linear infinite;
        }
        .animate-spin-slow {
          animation: spin 20s linear infinite;
        }
        .animate-spin-slow-reverse {
          animation: spin 25s linear infinite reverse;
        }
      `}</style>
        </div>
    );
};

const WhimsicalBackground = memo(() => (
    <WhimsicalBg />
));


export default WhimsicalBackground;
